import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { loadPayoutsFromLocalStorage, loadSettingsFromLocalStorage } from '@/utils/storage';
import { loadMiningConfigs } from '@/utils/miningStorage';
import { payoutApiService } from '@/services/payoutApiService';

const MIGRATION_DISMISSED_KEY = 'cryptotrack_migration_dismissed';

export function MigrationModal() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();

  const [show, setShow] = useState(false);
  const [payoutCount, setPayoutCount] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Check if already dismissed for this user
    const dismissed = localStorage.getItem(`${MIGRATION_DISMISSED_KEY}_${user?.id}`);
    if (dismissed) return;

    // Check if there's local data to migrate
    const hasEncryptedPayouts = localStorage.getItem('cryptotrack_encrypted_payouts');
    const hasUnencryptedPayouts = localStorage.getItem('cryptotrack_payouts');

    if (!hasEncryptedPayouts && !hasUnencryptedPayouts) return;

    // Count local payouts
    loadPayoutsFromLocalStorage().then((payouts) => {
      if (payouts.length > 0) {
        setPayoutCount(payouts.length);
        setShow(true);
      }
    });
  }, [isAuthenticated, user?.id]);

  const handleDismiss = () => {
    localStorage.setItem(`${MIGRATION_DISMISSED_KEY}_${user?.id}`, '1');
    setShow(false);
  };

  const handleImport = async () => {
    setIsImporting(true);
    setImportError(null);

    try {
      const [payouts, settings, miningConfigs] = await Promise.all([
        loadPayoutsFromLocalStorage(),
        loadSettingsFromLocalStorage(),
        loadMiningConfigs(),
      ]);

      const result = await payoutApiService.bulkImport(payouts, { settings, miningConfigs });
      setImportResult(result);

      // Invalidate queries to reload from API
      queryClient.invalidateQueries({ queryKey: ['payouts'] });

      // Mark as dismissed
      localStorage.setItem(`${MIGRATION_DISMISSED_KEY}_${user?.id}`, '1');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
    } finally {
      setIsImporting(false);
    }
  };

  const handleDone = () => {
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        {importResult ? (
          // Success state
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 text-center mb-2">Import terminé</h2>
            <p className="text-gray-600 dark:text-gray-400 text-center text-sm mb-4">
              <span className="font-semibold text-green-600">{importResult.imported}</span> transaction{importResult.imported > 1 ? 's' : ''} importée{importResult.imported > 1 ? 's' : ''}
              {importResult.skipped > 0 && (
                <>, <span className="font-semibold text-gray-500 dark:text-gray-400">{importResult.skipped}</span> ignorée{importResult.skipped > 1 ? 's' : ''} (déjà existantes)</>
              )}.
            </p>
            <button
              onClick={handleDone}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Fermer
            </button>
          </>
        ) : (
          // Prompt state
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
              Données locales détectées
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-center text-sm mb-4">
              {payoutCount} transaction{payoutCount > 1 ? 's' : ''} trouvée{payoutCount > 1 ? 's' : ''} dans le stockage local. Voulez-vous les importer dans votre compte pour les synchroniser sur tous vos appareils ?
            </p>

            {importError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {importError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleDismiss}
                disabled={isImporting}
                className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-50 hover:dark:bg-gray-900 transition-colors font-medium disabled:opacity-50"
              >
                Ignorer
              </button>
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                {isImporting ? 'Import...' : 'Importer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
